<template>
    <vx-expandable-card>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div data-test="entity-type-select">
                    <vx-label size="md">Entity type</vx-label>
                    <vx-selectable
                        v-model="form.entity_type"
                        size="lg"
                        :options="entityTypeOptions"
                        :reduce="(option: EntityTypeOption) => option.value"
                        :loading="null === props.enums"
                        :clearable="false"
                        :disabled="props.identityLocked"
                        :error="displayFirstError(validate.entity_type.$errors)"
                    ></vx-selectable>
                </div>

                <div data-test="ein-issuing-country-select">
                    <vx-label size="md">EIN issuing country</vx-label>
                    <vx-selectable
                        v-model="form.ein_issuing_country"
                        size="lg"
                        :options="einIssuingCountryOptions"
                        :reduce="(option: EinIssuingCountryOption) => option.value"
                        :loading="null === props.enums"
                        :clearable="false"
                        :disabled="props.identityLocked"
                        :error="displayFirstError(validate.ein_issuing_country.$errors)"
                    ></vx-selectable>
                </div>

                <template v-if="'PUBLIC_PROFIT' === form.entity_type">
                    <div>
                        <vx-label size="md">Stock symbol</vx-label>
                        <vx-input
                            v-model="form.stock_symbol"
                            size="lg"
                            data-test="stock-symbol-input"
                            :error="displayFirstError(validate.stock_symbol.$errors)"
                        ></vx-input>
                    </div>
                    <div data-test="stock-exchange-select">
                        <vx-label size="md">Stock exchange</vx-label>
                        <vx-selectable
                            v-model="form.stock_exchange"
                            size="lg"
                            :options="props.enums?.stock_exchanges ?? []"
                            :loading="null === props.enums"
                            :clearable="false"
                            :error="displayFirstError(validate.stock_exchange.$errors)"
                        ></vx-selectable>
                    </div>
                    <div>
                        <vx-label size="md">Business contact email</vx-label>
                        <vx-input
                            v-model="form.business_contact_email"
                            type="email"
                            size="lg"
                            data-test="business-contact-email-input"
                            :error="displayFirstError(validate.business_contact_email.$errors)"
                        ></vx-input>
                    </div>
                </template>

                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <vx-label size="md">Alternate business id</vx-label>
                        <vx-input
                            v-model="form.alt_business_id"
                            size="lg"
                            data-test="alt-business-id-input"
                            :error="displayFirstError(validate.alt_business_id.$errors)"
                        ></vx-input>
                    </div>
                    <div data-test="alt-business-id-type-select">
                        <vx-label size="md">Alternate business id type</vx-label>
                        <vx-selectable
                            v-model="form.alt_business_id_type"
                            size="lg"
                            :options="altBusinessIdTypeOptions"
                            :reduce="(option: AltBusinessIdTypeOption) => option.value"
                            :clearable="false"
                            :error="displayFirstError(validate.alt_business_id_type.$errors)"
                        ></vx-selectable>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { email, maxLength, required, requiredIf } from '@vuelidate/validators';
import { computed, toRef } from 'vue';
import { einIssuingCountryLabels } from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { entityTypeLabels } from '~/components/kiosk/kiosk-ten-dlc/tenDlcLabels';
import type { TcrEntityType } from '~/services/adminTenDlcClient.types';
import type {
    TcrBrandCreate,
    TcrEinIssuingCountry,
    TcrEntityTypeCode,
    TcrEnums,
} from '~/services/adminTenDlcRegistrationClient.types';
import { displayFirstError } from '~/utils/validation';

type EntityTypeOption = { label: string; value: TcrEntityTypeCode };
type EinIssuingCountryOption = { label: string; value: TcrEinIssuingCountry };
type AltBusinessIdTypeOption = { label: string; value: string };

// TcrBrandPayload::ALT_BUSINESS_ID_TYPES; a blank is omitted on create, and the edit modal sends NONE for it.
const altBusinessIdTypeOptions: AltBusinessIdTypeOption[] = [
    { label: 'None', value: '' },
    { label: 'DUNS', value: 'DUNS' },
    { label: 'GIIN', value: 'GIIN' },
    { label: 'LEI', value: 'LEI' },
];

const props = defineProps<{
    enums: TcrEnums | null;
    externalResults: Record<string, string[]>;
    identityLocked?: boolean;
}>();
const form = defineModel<TcrBrandCreate>({ required: true });

const entityTypeOptions = computed<EntityTypeOption[]>(() =>
    (props.enums?.entity_types ?? []).map((value) => ({
        value,
        label: entityTypeLabels[value.toLowerCase() as TcrEntityType],
    }))
);

const einIssuingCountryOptions = computed<EinIssuingCountryOption[]>(() =>
    (props.enums?.ein_issuing_countries ?? []).map((value) => ({ value, label: einIssuingCountryLabels[value] }))
);

const isPublic = () => 'PUBLIC_PROFIT' === form.value.entity_type;

const validate = useVuelidate(
    {
        entity_type: { required },
        ein_issuing_country: { required },
        stock_symbol: { requiredIf: requiredIf(isPublic), maxLength: maxLength(10) },
        stock_exchange: { requiredIf: requiredIf(isPublic) },
        business_contact_email: { requiredIf: requiredIf(isPublic), email, maxLength: maxLength(255) },
        alt_business_id: { maxLength: maxLength(50) },
        alt_business_id_type: {},
    },
    form,
    { $externalResults: toRef(props, 'externalResults') }
);
</script>
